import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sparkles, Brain, AlertTriangle, Loader2, Award, TrendingDown, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const LEVEL_COLORS = {
  critique: "bg-red-100 text-red-800 border-red-300",
  "élevé": "bg-orange-100 text-orange-800 border-orange-300",
  "modéré": "bg-yellow-100 text-yellow-800 border-yellow-300",
  faible: "bg-slate-100 text-slate-700 border-slate-300",
};

export default function IAPedagogiePage() {
  const [quizzes, setQuizzes] = useState([]);
  const [risks, setRisks] = useState(null);
  const [openQuiz, setOpenQuiz] = useState(false);
  const [openRisk, setOpenRisk] = useState(false);
  const [openReco, setOpenReco] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reco, setReco] = useState({ student: null, text: "" });
  const [quizForm, setQuizForm] = useState({ subject: "", topic: "", class_level: "", num_questions: 10, difficulty: "moyen" });
  const [generatedQuiz, setGeneratedQuiz] = useState(null);
  const [tab, setTab] = useState("quiz");

  const load = async () => {
    try {
      const [{ data: qs }, { data: r }] = await Promise.all([
        api.get("/pedagogy-ai/quiz"),
        api.get("/pedagogy-ai/risk-detection"),
      ]);
      setQuizzes(qs); setRisks(r);
    } catch { toast.error("Erreur"); }
  };
  useEffect(() => { load(); }, []);

  const generateQuiz = async () => {
    if (!quizForm.subject || !quizForm.topic || !quizForm.class_level) return toast.error("Matière, sujet, niveau requis");
    setSaving(true);
    try {
      const { data } = await api.post("/pedagogy-ai/quiz/generate", { ...quizForm, num_questions: Number(quizForm.num_questions) });
      toast.success(`Quiz généré ! ${data.questions.length} questions`);
      setGeneratedQuiz(data);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur IA"); }
    finally { setSaving(false); }
  };

  const askReco = async (student) => {
    setReco({ student, text: "" });
    setOpenReco(true);
    try {
      const { data } = await api.post(`/pedagogy-ai/risk/${student.student_id}/ai-recommendation`);
      setReco({ student: data.student, text: data.recommendations });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur IA");
      setReco({ ...reco, text: "Erreur lors de la génération." });
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-pedagogie">
      <div>
        <div className="chip mb-2"><Sparkles className="h-3 w-3" /><span>IA PÉDAGOGIQUE</span></div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Assistant IA pédagogique</h1>
        <p className="text-slate-500 mt-1">Génération de quiz, détection des élèves à risque et recommandations personnalisées.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button onClick={() => setTab("quiz")} className={`px-4 py-2 font-semibold text-sm border-b-2 -mb-px ${tab === "quiz" ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-slate-500"}`} data-testid="tab-quiz">
          <Brain className="h-4 w-4 inline mr-1" />Générateur de quiz
        </button>
        <button onClick={() => setTab("risk")} className={`px-4 py-2 font-semibold text-sm border-b-2 -mb-px ${tab === "risk" ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-slate-500"}`} data-testid="tab-risk">
          <AlertTriangle className="h-4 w-4 inline mr-1" />Détection risques ({risks?.at_risk_count || 0})
        </button>
      </div>

      {tab === "quiz" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <h2 className="font-heading font-bold text-lg text-[#1E3A8A] mb-3">Générer un quiz avec l&apos;IA</h2>
            <div className="space-y-3">
              <div><Label>Matière *</Label><Input value={quizForm.subject} onChange={e => setQuizForm({...quizForm, subject: e.target.value})} placeholder="Mathématiques" data-testid="q-subject" /></div>
              <div><Label>Sujet / Thème *</Label><Input value={quizForm.topic} onChange={e => setQuizForm({...quizForm, topic: e.target.value})} placeholder="Les fractions" data-testid="q-topic" /></div>
              <div><Label>Niveau *</Label><Input value={quizForm.class_level} onChange={e => setQuizForm({...quizForm, class_level: e.target.value})} placeholder="CE2, 6ème..." data-testid="q-level" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nombre de questions</Label><Input type="number" min="3" max="30" value={quizForm.num_questions} onChange={e => setQuizForm({...quizForm, num_questions: e.target.value})} data-testid="q-num" /></div>
                <div>
                  <Label>Difficulté</Label>
                  <Select value={quizForm.difficulty} onValueChange={(v) => setQuizForm({...quizForm, difficulty: v})}>
                    <SelectTrigger data-testid="q-diff"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="facile">Facile</SelectItem><SelectItem value="moyen">Moyen</SelectItem><SelectItem value="difficile">Difficile</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={generateQuiz} disabled={saving} className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="q-generate">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}Générer avec l&apos;IA
              </Button>
            </div>
          </div>

          {generatedQuiz && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm max-h-[70vh] overflow-y-auto" data-testid="generated-quiz">
              <h2 className="font-heading font-bold text-lg text-[#1E3A8A] mb-3">Quiz généré ({generatedQuiz.questions.length} questions)</h2>
              <div className="space-y-4">
                {generatedQuiz.questions.map((q, i) => (
                  <div key={i} className="border border-slate-200 rounded-lg p-3">
                    <div className="font-semibold text-slate-800 mb-2">Q{i+1}. {q.question}</div>
                    <ul className="space-y-1 text-sm">
                      {q.choices.map((c, j) => (
                        <li key={j} className={j === q.correct_index ? "text-green-700 font-bold" : "text-slate-600"}>{c}{j === q.correct_index && " ✓"}</li>
                      ))}
                    </ul>
                    {q.explanation && <div className="text-xs text-slate-500 mt-2 italic bg-slate-50 p-2 rounded">💡 {q.explanation}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "risk" && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-testid="risk-section">
          <div className="px-5 py-3 border-b border-slate-200 bg-orange-50">
            <h2 className="font-heading font-bold text-lg text-[#EA580C]">Élèves à risque d&apos;échec scolaire</h2>
            <div className="text-xs text-slate-500">Analyse basée sur : moyennes, absences, retards. {risks?.total_analyzed || 0} élèves analysés · {risks?.at_risk_count || 0} détectés.</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Élève</th>
                  <th className="px-4 py-3">Classe</th>
                  <th className="px-4 py-3">Moyenne</th>
                  <th className="px-4 py-3">Absences</th>
                  <th className="px-4 py-3">Score de risque</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {(risks?.students || []).map(s => (
                  <tr key={s.student_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`risk-${s.student_id}`}>
                    <td className="px-4 py-3 font-semibold">{s.student_name}</td>
                    <td className="px-4 py-3 text-slate-500">{s.class_name}</td>
                    <td className="px-4 py-3 tabular-nums font-bold">{s.average}/20</td>
                    <td className="px-4 py-3 tabular-nums">{s.absence_count} ({s.absence_rate}%)</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold border ${LEVEL_COLORS[s.risk_level]}`}>
                        {s.risk_level.toUpperCase()} · {s.risk_score}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" onClick={() => askReco(s)} className="bg-purple-600 text-white" data-testid={`reco-${s.student_id}`}>
                        <Sparkles className="h-3 w-3 mr-1" />Recommandation IA
                      </Button>
                    </td>
                  </tr>
                ))}
                {(risks?.students || []).length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">Aucun élève à risque détecté.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={openReco} onOpenChange={setOpenReco}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-purple-600" />Recommandation IA — {reco.student?.name}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {reco.text ? (
              <div className="prose prose-sm max-w-none whitespace-pre-wrap p-3 bg-purple-50 rounded-lg">{reco.text}</div>
            ) : (
              <div className="text-center py-8 text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline mr-2" />L&apos;IA analyse le dossier...</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
